import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Users, Trash2, Download, ShieldCheck, Shield, 
  UserCheck, UserX, AlertTriangle, X, Key, Mail, Lock, Pencil
} from 'lucide-react';
import {
  deleteUser,
  deleteUserSeasonData,
  updateUserRole,
  toggleUserActivation,
  toggleUserPaymentStatus,
  exportUsersCSV,
  adminSetTemporaryPassword,
  adminSendPasswordResetEmail,
  adminUpdateUser,
} from '../../services/api';

const UsersTab = ({ users, refreshUsers, user, toast }) => {
  // Local state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '', first_name: '', last_name: '', email: '', employee_number: ''
  });

  const handleExportUsers = async () => {
    try {
      const blob = await exportUsersCSV();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      if (toast) toast({
        title: 'Export gelukt',
        description: 'Gebruikerslijst is gedownload',
      });
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: 'Kon export niet maken',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteUser = async (userId, username, skipToast = false) => {
    if (!skipToast && !window.confirm(`Weet je zeker dat je ${username} wilt verwijderen?`)) {
      return;
    }
    
    try {
      await deleteUser(userId);
      if (!skipToast && toast) {
        toast({
          title: 'Gebruiker verwijderd',
          description: `${username} is verwijderd`,
        });
      }
      if (refreshUsers) await refreshUsers();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon gebruiker niet verwijderen',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteUserSeasonData = async (userId, username, season) => {
    if (!window.confirm(`Weet je zeker dat je de ${season} data van ${username} wilt verwijderen?`)) {
      return;
    }
    
    try {
      await deleteUserSeasonData(userId, season);
      if (toast) toast({
        title: 'Data verwijderd',
        description: `${season} data van ${username} is verwijderd`,
      });
      if (refreshUsers) await refreshUsers();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon data niet verwijderen',
        variant: 'destructive',
      });
    }
  };

  const handleToggleAdminRole = async (userId, username, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const action = currentRole === 'admin' ? 'verwijderen' : 'toewijzen';
    
    if (!window.confirm(`Weet je zeker dat je admin rechten wilt ${action} voor ${username}?`)) {
      return;
    }
    
    try {
      await updateUserRole(userId, newRole);
      if (toast) toast({
        title: 'Rol bijgewerkt',
        description: `${username} is nu ${newRole === 'admin' ? 'Admin' : 'een gewone gebruiker'}`,
      });
      if (refreshUsers) await refreshUsers();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon rol niet bijwerken',
        variant: 'destructive',
      });
    }
  };

  const handleToggleUserActivation = async (userId, username, isActive) => {
    const action = isActive ? 'deactiveren' : 'activeren';
    
    if (!window.confirm(`Weet je zeker dat je ${username} wilt ${action}?`)) {
      return;
    }
    
    try {
      await toggleUserActivation(userId, !isActive);
      if (toast) toast({
        title: isActive ? 'Gebruiker gedeactiveerd' : 'Gebruiker geactiveerd',
        description: `${username} is nu ${isActive ? 'gedeactiveerd' : 'geactiveerd'}`,
      });
      if (refreshUsers) await refreshUsers();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon status niet bijwerken',
        variant: 'destructive',
      });
    }
  };

  const handleTogglePaymentStatus = async (userId, username, hasPaid) => {
    try {
      await toggleUserPaymentStatus(userId, !hasPaid);
      if (toast) toast({
        title: hasPaid ? 'Betaling ongedaan' : 'Betaling gemarkeerd',
        description: `${username} is nu gemarkeerd als ${hasPaid ? 'niet betaald' : 'betaald'}`,
      });
      if (refreshUsers) await refreshUsers();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon betalingsstatus niet bijwerken',
        variant: 'destructive',
      });
    }
  };

  // Password management functions
  const handleSetTemporaryPassword = async () => {
    if (!passwordModalUser || !temporaryPassword) return;
    
    try {
      await adminSetTemporaryPassword(passwordModalUser.id, temporaryPassword);
      if (toast) toast({
        title: 'Wachtwoord ingesteld',
        description: `Tijdelijk wachtwoord ingesteld voor ${passwordModalUser.username}. Gebruiker moet wachtwoord wijzigen bij volgende login.`,
      });
      setShowPasswordModal(false);
      setPasswordModalUser(null);
      setTemporaryPassword('');
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon wachtwoord niet instellen',
        variant: 'destructive',
      });
    }
  };

  const handleSendPasswordResetEmail = async (targetUser) => {
    try {
      await adminSendPasswordResetEmail(targetUser.id);
      if (toast) toast({
        title: 'Reset link verzonden',
        description: `Wachtwoord reset link is verzonden naar ${targetUser.email}`,
      });
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon reset email niet verzenden',
        variant: 'destructive',
      });
    }
  };

  const openPasswordModal = (targetUser) => {
    setPasswordModalUser(targetUser);
    setTemporaryPassword('');
    setShowPasswordModal(true);
  };

  const openEditModal = (targetUser) => {
    setEditingUser(targetUser);
    setEditForm({
      username: targetUser.username || '',
      first_name: targetUser.first_name || '',
      last_name: targetUser.last_name || '',
      email: targetUser.email || '',
      employee_number: targetUser.employee_number || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    try {
      await adminUpdateUser(editingUser.id, editForm);
      if (toast) toast({
        title: 'Gebruiker bijgewerkt',
        description: `${editForm.username} is succesvol bijgewerkt`,
      });
      setShowEditModal(false);
      setEditingUser(null);
      if (refreshUsers) await refreshUsers();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon gebruiker niet bijwerken',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Users className="mr-2 text-[#FDB913]" size={20} />
            Gebruikers Beheer
          </div>
          <div className="flex items-center space-x-2">
            {selectedUsers.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="mr-2" size={16} />
                Verwijder ({selectedUsers.length})
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportUsers}
              className="bg-green-500/20 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-400 hover:bg-green-500/30"
            >
              <Download className="mr-2" size={16} />
              Exporteer Lijst (CSV)
            </Button>
          </div>
        </CardTitle>
        <CardDescription>
          Beheer gebruikers en hun voorspellingen. {users.length} gebruikers totaal.
          {selectedUsers.length > 0 && (
            <span className="ml-2 text-blue-600 font-medium">
              ({selectedUsers.length} geselecteerd)
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Bulk Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-red-600 flex items-center mb-4">
                <AlertTriangle className="mr-2" size={24} />
                Bevestig Verwijdering
              </h3>
              
              {(() => {
                const selectedUserObjects = users.filter(u => selectedUsers.includes(u.id));
                const adminUsers = selectedUserObjects.filter(u => u.role === 'admin' || u.role === 'super_admin');
                const currentUserSelected = selectedUserObjects.some(u => u.id === user?.id);
                
                return (
                  <>
                    {currentUserSelected && (
                      <div className="bg-red-500/20 dark:bg-red-900/40 border-2 border-red-500 rounded-lg p-3 mb-4">
                        <p className="text-red-700 dark:text-red-400 font-bold flex items-center">
                          <X className="mr-2" size={18} />
                          Je kunt jezelf niet verwijderen!
                        </p>
                      </div>
                    )}
                    
                    {adminUsers.length > 0 && (
                      <div className="bg-orange-500/20 dark:bg-orange-900/40 border-2 border-orange-500 rounded-lg p-3 mb-4">
                        <p className="text-orange-700 dark:text-orange-400 font-bold mb-2 flex items-center">
                          <ShieldCheck className="mr-2" size={18} />
                          ⚠️ Let op: Admin gebruikers geselecteerd!
                        </p>
                        <ul className="text-sm text-orange-600 dark:text-orange-400 list-disc list-inside">
                          {adminUsers.map(u => (
                            <li key={u.id}>
                              <strong>{u.username}</strong> ({u.role === 'super_admin' ? 'Super Admin' : 'Admin'})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="mb-4">
                      <p className="text-muted-foreground mb-2">
                        Je staat op het punt <strong>{selectedUsers.length}</strong> gebruiker(s) te verwijderen:
                      </p>
                      <div className="max-h-40 overflow-y-auto bg-secondary rounded p-2">
                        {selectedUserObjects.map(u => (
                          <div key={u.id} className={`text-sm py-1 px-2 rounded mb-1 ${
                            u.role === 'super_admin' ? 'bg-[#1a2332] text-[#FDB913]' :
                            u.role === 'admin' ? 'bg-orange-500/20 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400' :
                            'bg-card'
                          }`}>
                            {u.username} 
                            {u.role === 'super_admin' && ' 🛡️ Super Admin'}
                            {u.role === 'admin' && ' 🛡️ Admin'}
                            {u.id === user?.id && ' (JIJ)'}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-4">
                      Dit verwijdert ook alle voorspellingen van deze gebruikers. 
                      Deze actie kan niet ongedaan worden gemaakt.
                    </p>
                    
                    <div className="flex space-x-3">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Annuleren
                      </Button>
                      <Button
                        variant="destructive"
                        className="flex-1"
                        disabled={currentUserSelected}
                        onClick={async () => {
                          const usersToDelete = selectedUsers.filter(id => id !== user?.id);
                          
                          for (const userId of usersToDelete) {
                            const u = users.find(usr => usr.id === userId);
                            if (u) {
                              await handleDeleteUser(userId, u.username, true);
                            }
                          }
                          
                          setSelectedUsers([]);
                          setShowDeleteConfirm(false);
                          if (toast) toast({
                            title: "Gebruikers verwijderd",
                            description: `${usersToDelete.length} gebruiker(s) succesvol verwijderd.`,
                          });
                        }}
                      >
                        <Trash2 className="mr-2" size={16} />
                        Definitief Verwijderen
                      </Button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {users.length === 0 ? (
          <p className="text-muted-foreground">Geen gebruikers gevonden</p>
        ) : (
          <div className="space-y-4">
            {/* Select All Header */}
            <div className="flex items-center space-x-3 p-3 bg-secondary rounded-lg">
              <Checkbox
                checked={
                  selectedUsers.length > 0 && 
                  selectedUsers.length === users.filter(u => 
                    u.id !== user?.id && 
                    u.role !== 'admin' && 
                    u.role !== 'super_admin'
                  ).length
                }
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSelectedUsers(users.filter(u => 
                      u.id !== user?.id && 
                      u.role !== 'admin' && 
                      u.role !== 'super_admin'
                    ).map(u => u.id));
                  } else {
                    setSelectedUsers([]);
                  }
                }}
              />
              <span className="text-sm font-medium text-muted-foreground">
                Selecteer alle reguliere gebruikers (excl. Admins)
              </span>
            </div>

            {users
              .slice()
              .sort((a, b) => a.username.localeCompare(b.username))
              .map((u) => (
              <div key={u.id} className={`flex items-center justify-between p-4 border rounded-lg hover:bg-secondary ${
                selectedUsers.includes(u.id) ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300' : ''
              }`}>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    checked={selectedUsers.includes(u.id)}
                    disabled={u.id === user?.id || u.role === 'admin' || u.role === 'super_admin'}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedUsers([...selectedUsers, u.id]);
                      } else {
                        setSelectedUsers(selectedUsers.filter(id => id !== u.id));
                      }
                    }}
                  />
                  
                  {(u.role === 'admin' || u.role === 'super_admin') && (
                    <div className={`rounded-full p-1.5 ${
                      u.role === 'super_admin' ? 'bg-[#1a2332]' : 'bg-[#FDB913]'
                    }`}>
                      <ShieldCheck size={16} className={u.role === 'super_admin' ? 'text-[#FDB913]' : 'text-[#1a2332]'} />
                    </div>
                  )}
                  <div>
                    <div className="font-semibold flex items-center flex-wrap gap-1">
                      {u.username}
                      {u.id === user?.id && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                          Jij
                        </span>
                      )}
                      {u.role === 'super_admin' && (
                        <span className="text-xs bg-[#1a2332] text-[#FDB913] px-2 py-0.5 rounded-full font-semibold">
                          Super Admin
                        </span>
                      )}
                      {u.role === 'admin' && u.role !== 'super_admin' && (
                        <span className="text-xs bg-[#FDB913] text-[#1a2332] px-2 py-0.5 rounded-full">
                          Admin
                        </span>
                      )}
                      {u.is_active ? (
                        <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full flex items-center">
                          <UserCheck size={10} className="mr-1" />
                          Actief
                        </span>
                      ) : (
                        <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full flex items-center">
                          <UserX size={10} className="mr-1" />
                          Inactief
                        </span>
                      )}
                      {u.has_paid ? (
                        <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full flex items-center">
                          💰 Betaald
                        </span>
                      ) : (
                        <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full flex items-center">
                          ⚠️ Niet betaald
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">{u.email}</div>
                    {(u.first_name || u.last_name) && (
                      <div className="text-sm text-foreground">
                        {u.first_name} {u.last_name}
                      </div>
                    )}
                    {u.employee_number && (
                      <div className="text-sm text-blue-600 dark:text-blue-400 font-mono">
                        📋 {u.employee_number}
                      </div>
                    )}
                    <div className="text-sm text-muted-foreground mt-1">
                      {u.points} punten | {u.total_predictions} voorspellingen
                      {u.seasons_participated?.length > 0 && (
                        <span> | Seizoenen: {u.seasons_participated.join(', ')}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(u)}
                    className="border-[#FDB913] text-[#FDB913] hover:bg-[#FDB913]/10"
                    data-testid={`edit-user-${u.id}`}
                  >
                    <Pencil size={14} className="mr-1" />
                    Bewerk
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleUserActivation(u.id, u.username, u.is_active)}
                    disabled={u.id === user?.id || u.role === 'super_admin'}
                    className={u.is_active ? 'border-red-500 text-red-600 hover:bg-red-50' : 'border-green-500 text-green-600 hover:bg-green-50'}
                  >
                    {u.is_active ? (
                      <>
                        <UserX size={14} className="mr-1" />
                        Deactiveer
                      </>
                    ) : (
                      <>
                        <UserCheck size={14} className="mr-1" />
                        Activeer
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTogglePaymentStatus(u.id, u.username, u.has_paid)}
                    disabled={u.role === 'super_admin'}
                    className={u.has_paid ? 'border-orange-500 text-orange-600 hover:bg-orange-50' : 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'}
                  >
                    {u.has_paid ? (
                      <>💰 Betaald ✓</>
                    ) : (
                      <>💰 Markeer betaald</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleAdminRole(u.id, u.username, u.role || 'user')}
                    disabled={u.id === user?.id || u.role === 'super_admin'}
                    className={u.role === 'admin' ? 'border-orange-500 text-orange-600' : 'border-green-500 text-green-600'}
                  >
                    {u.role === 'admin' ? (
                      <>
                        <Shield size={14} className="mr-1" />
                        Admin af
                      </>
                    ) : (
                      <>
                        <ShieldCheck size={14} className="mr-1" />
                        Maak Admin
                      </>
                    )}
                  </Button>
                  
                  {/* Password Management Buttons */}
                  {u.id !== user?.id && u.role !== 'super_admin' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPasswordModal(u)}
                        className="border-purple-500 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                        title="Stel tijdelijk wachtwoord in"
                      >
                        <Key size={14} className="mr-1" />
                        Wachtwoord
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendPasswordResetEmail(u)}
                        className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        title="Verstuur reset link naar gebruiker"
                      >
                        <Mail size={14} className="mr-1" />
                        Reset Link
                      </Button>
                    </>
                  )}
                  
                  {u.seasons_participated?.map((season) => (
                    <Button
                      key={season}
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteUserSeasonData(u.id, u.username, season)}
                      className="text-orange-600 hover:text-orange-700"
                    >
                      <Trash2 size={14} className="mr-1" />
                      {season}
                    </Button>
                  ))}
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteUser(u.id, u.username)}
                    disabled={u.id === user?.id}
                  >
                    <Trash2 size={14} className="mr-1" />
                    Verwijder
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      
      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background border rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Pencil className="mr-2 text-[#FDB913]" size={20} />
                Gebruiker Bewerken
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                data-testid="edit-modal-close"
              >
                <X size={20} />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">Gebruikersnaam</Label>
                <Input
                  data-testid="edit-username"
                  value={editForm.username}
                  onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                  placeholder="Gebruikersnaam"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium mb-1 block">Voornaam</Label>
                  <Input
                    data-testid="edit-first-name"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                    placeholder="Voornaam"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1 block">Achternaam</Label>
                  <Input
                    data-testid="edit-last-name"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                    placeholder="Achternaam"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1 block">E-mailadres</Label>
                <Input
                  data-testid="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  placeholder="E-mailadres"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1 block">Personeelsnummer</Label>
                <Input
                  data-testid="edit-employee-number"
                  value={editForm.employee_number}
                  onChange={(e) => setEditForm({...editForm, employee_number: e.target.value})}
                  placeholder="Personeelsnummer"
                />
              </div>
              
              <div className="flex space-x-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                >
                  Annuleren
                </Button>
                <Button
                  className="flex-1 bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]"
                  onClick={handleSaveEdit}
                  data-testid="edit-save-btn"
                >
                  Opslaan
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && passwordModalUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background border rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Lock className="mr-2 text-purple-500" size={20} />
                Wachtwoord Beheer
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordModalUser(null);
                  setTemporaryPassword('');
                }}
              >
                <X size={20} />
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Gebruiker: <span className="font-semibold text-foreground">{passwordModalUser.username}</span>
              <br />
              Email: <span className="text-foreground">{passwordModalUser.email}</span>
            </p>
            
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <Label className="text-sm font-medium mb-2 block">
                  Optie 1: Tijdelijk Wachtwoord Instellen
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  De gebruiker moet dit wachtwoord wijzigen bij de volgende login.
                </p>
                <div className="flex space-x-2">
                  <Input
                    type="text"
                    placeholder="Voer tijdelijk wachtwoord in"
                    value={temporaryPassword}
                    onChange={(e) => setTemporaryPassword(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSetTemporaryPassword}
                    disabled={!temporaryPassword}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Key size={14} className="mr-1" />
                    Instellen
                  </Button>
                </div>
              </div>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <Label className="text-sm font-medium mb-2 block">
                  Optie 2: Wachtwoord Reset Link Versturen
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Stuurt een email met een reset link naar {passwordModalUser.email}
                </p>
                <Button
                  onClick={() => {
                    handleSendPasswordResetEmail(passwordModalUser);
                    setShowPasswordModal(false);
                    setPasswordModalUser(null);
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Mail size={14} className="mr-1" />
                  Verstuur Reset Link
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default UsersTab;
